import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ISelection } from 'pbc.types';

import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, ISitemapModel, SITEMAP } from 'pbc.angular';

import { IOrt, IPostOrtResponse } from 'fa-kt.types';
import { OrtKategoriesService } from '../../../../ort-kategories';
import { OrtsService } from '../../../query';
import { PostOrtCommandService } from '../service';

interface IFormValue {
  ort?: IOrt;
}

@Component({
  selector: 'fa-kt-post-ort',
  templateUrl: './post-ort.component.html',
  styleUrls: ['./post-ort.component.css'],
})
export class PostOrtCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;
  model!: ISitemapModel;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IFormValue) {
    this.patch(value);
  }
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostOrtResponse> = new EventEmitter<IPostOrtResponse>();

  $ortKategories = new BehaviorSubject<ISelection[]>([]);

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private actions: ActionService,
    private message: NzMessageService,
    public orts: OrtsService,
    public postOrt: PostOrtCommandService,
    public ortKategoriens: OrtKategoriesService /* << dependencies    */ /*    dependencies >> */,
  ) {
    this.command = sitemap.GEO.Commands.POST_ORT;
    this.model = sitemap.GEO.Models.ORT;
    /* << constructor    */
    /*    constructor >> */
    this.form = this.fb.group({
      ort: this.fb.group({
        id: [null, []],
        name: [null, [Validators.required]],
        kommentar: [null, []],
        ort: [null, [Validators.required]],
        ortKategorie: [null, [Validators.required]],
      }),
    });
    this.patch();
  }

  ngOnInit() {
    this.ortKategoriens.request({}).catch(console.error);
    this.subscriptions.push(
      this.ortKategoriens.result$.subscribe((result) => {
        if (!result) {
          return;
        }
        this.$ortKategories.next(result.ortKategories.map((ok) => ({ value: ok.id, label: ok.name })));
      }),
      this.form.valueChanges.subscribe((value: any) => {
        this.valid.emit(this.form.valid);
        if (this.form.valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.id?.valueChanges.subscribe(async (id) => {
        /* << on-id-change    */
        /*    on-id-change >> */
      }) as Subscription,
      this.name?.valueChanges.subscribe(async (name) => {
        /* << on-name-change    */
        /*    on-name-change >> */
      }) as Subscription,
      this.kommentar?.valueChanges.subscribe(async (kommentar) => {
        /* << on-kommentar-change    */
        /*    on-kommentar-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IFormValue) {
    let ort = {
      id: null,
      name: null,
      kommentar: null,
      ort: null,
      ortKategorie: null,
    };
    if (value && value.ort) {
      ort = {
        ...ort,
        ...cleanObject(value.ort),
      };
    }
    this.form.patchValue({ ort });
    /* << custom-stuff    */
    /*    custom-stuff >> */
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload && payload.ort) {
        payload.ort = cleanObject(payload.ort);
      }
      const response: IPostOrtResponse | undefined = await this.postOrt.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls.objektArt.get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get id() {
    return this.form.controls.ort.get('id');
  }

  get name() {
    return this.form.controls.ort.get('name');
  }

  get kommentar() {
    return this.form.controls.ort.get('kommentar');
  }
}
